// ============================================================================
// OrgFocusView — vue focus incrementale desktop (NAV v3)
// ============================================================================
// Manager (1 carte) + focus (centre) + reports directs en rangee ; bouton +
// = apercu des reports d'un subordonne sans changer le focus.

import { GS_COMPONENTS } from '@gs/ui';
import {
  collectPathEmails,
  directOrgReports,
  findOrgNode,
  type OrgNode,
} from '../../lib/org/hierarchy';
import { useTranslation } from '../../i18n';
import { OrgFocusCard } from './OrgFocusCard';

function getManagerNode(roots: OrgNode[], focusedEmail: string): OrgNode | null {
  const path = collectPathEmails(roots, focusedEmail);
  if (path.length < 2) return null;
  return findOrgNode(roots, path[path.length - 2]);
}

export function OrgFocusView({
  roots,
  focusedEmail,
  highlightedEmail,
  previewEmail,
  onFocus,
  onPreviewToggle,
}: {
  roots: OrgNode[];
  focusedEmail: string;
  highlightedEmail?: string | null;
  previewEmail?: string | null;
  onFocus: (email: string) => void;
  onPreviewToggle: (email: string | null) => void;
}) {
  const { t } = useTranslation();
  const focusedNode = findOrgNode(roots, focusedEmail);
  if (!focusedNode) return null;

  const manager = getManagerNode(roots, focusedEmail);
  const reports = directOrgReports(focusedNode);

  return (
    <section
      aria-label={t('gest:organigramme.view.focus')}
      className={`${GS_COMPONENTS.card} flex min-h-96 flex-col items-center gap-0 bg-slate-50 p-8 dark:bg-slate-900`}
    >
      <div className="mb-5 text-center">
        {focusedNode.team_name ? (
          <p className="text-field font-semibold text-slate-700 dark:text-slate-200">
            {focusedNode.team_name}
          </p>
        ) : null}
        <p className="mt-1 text-meta text-slate-500 dark:text-slate-400">
          {t('gest:organigramme.focus.branchCount', { count: focusedNode.descendant_count })}
        </p>
      </div>

      <div className="flex w-full max-w-5xl flex-col items-center">
        {manager ? (
          <>
            <p className="mb-2 text-meta font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {t('gest:organigramme.focus.managerLabel')}
            </p>
            <OrgFocusCard
              node={manager}
              variant="manager"
              isHighlighted={highlightedEmail === manager.email}
              onFocus={() => onFocus(manager.email)}
            />
            <div className="my-1 h-5 w-0.5 bg-slate-300 dark:bg-slate-600" aria-hidden="true" />
          </>
        ) : null}

        <OrgFocusCard
          node={focusedNode}
          variant="focus"
          showAccentBar={!manager}
          isHighlighted={highlightedEmail === focusedNode.email}
          onFocus={() => onFocus(focusedNode.email)}
        />

        {reports.length > 0 ? (
          <div className="my-1 h-5 w-0.5 bg-slate-300 dark:bg-slate-600" aria-hidden="true" />
        ) : null}

        <div className="mt-2 w-full">
          <p className="mb-3 text-center text-meta font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {t('gest:organigramme.focus.reportsLabel', { count: reports.length })}
          </p>
          {reports.length === 0 ? (
            <p className="text-center text-field text-slate-500 dark:text-slate-400">
              {t('gest:organigramme.focus.noReports')}
            </p>
          ) : (
            <div className="flex flex-wrap items-start justify-center gap-4">
              {reports.map((report) => {
                const subReports = directOrgReports(report);
                const previewOpen = previewEmail === report.email;
                return (
                  <div key={report.email} className="flex flex-col items-center gap-3">
                    <OrgFocusCard
                      node={report}
                      variant="report"
                      directReportsCount={subReports.length}
                      isHighlighted={highlightedEmail === report.email}
                      showDeepen={subReports.length > 0}
                      isDeepenOpen={previewOpen}
                      onFocus={() => onFocus(report.email)}
                      onDeepen={() => onPreviewToggle(previewOpen ? null : report.email)}
                    />
                    {previewOpen && subReports.length > 0 ? (
                      <div className="flex w-full max-w-md flex-col items-center gap-2 border-t border-dashed border-slate-300 pt-3 dark:border-slate-600">
                        <p className="text-chip text-slate-400 dark:text-slate-500">
                          {t('gest:organigramme.focus.previewLabel', {
                            name: report.full_name ?? report.email,
                          })}
                        </p>
                        <div className="flex flex-wrap justify-center gap-3">
                          {subReports.map((child) => (
                            <OrgFocusCard
                              key={child.email}
                              node={child}
                              variant="preview"
                            />
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
